import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg md:text-xl font-semibold text-[#1B4D3E] mb-3">{title}</h2>
        <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">{children}</div>
    </section>
);

export default function PrivacyPolicyPage() {
    return (
        <div className="bg-slate-50 min-h-screen py-12" data-testid="privacy-policy-page">
            <div className="container-main max-w-3xl">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-10">
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
                    <p className="text-sm text-slate-400 mb-8">Last updated: June 2026</p>

                    <Section title="1. Introduction">
                        <p>
                            This Privacy Policy explains how <strong>Henry's Aussie Fruit &amp; Veggies</strong>
                            ("we", "us", "our") collects, uses, and protects your information when you use our
                            website and wholesale ordering platform at{' '}
                            <a href="https://www.henrysaussiefv.com.au" className="text-[#1B4D3E] underline">
                                www.henrysaussiefv.com.au
                            </a>{' '}
                            (the "Service"). By using the Service, you agree to the practices described here.
                        </p>
                    </Section>

                    <Section title="2. Information We Collect">
                        <p>We collect the following information to provide and improve our Service:</p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Account information</strong> — your name, email address, business name, and (where you register with email) a password.</li>
                            <li><strong>Google account information</strong> — if you choose to sign in with Google, we receive your basic Google profile: name, email address, and profile picture.</li>
                            <li><strong>Order &amp; delivery information</strong> — the products you order, quantities, delivery or pickup details, address, suburb, postcode, and any notes you provide.</li>
                            <li><strong>Payment information</strong> — payments are handled by our payment processor (Stripe). We do <strong>not</strong> collect or store your full card details.</li>
                            <li><strong>Usage information</strong> — basic technical data such as browser type and interactions needed to operate and secure the Service.</li>
                        </ul>
                    </Section>

                    <Section title="3. How We Use Your Information">
                        <ul className="list-disc pl-6 space-y-1">
                            <li>To create and authenticate your account and keep you signed in.</li>
                            <li>To process, prepare, price (based on actual weighed quantities), and fulfil your orders.</li>
                            <li>To send you transactional emails such as order confirmations and payment links.</li>
                            <li>To respond to your enquiries and provide customer support.</li>
                            <li>To operate, maintain, secure, and improve the Service.</li>
                        </ul>
                    </Section>

                    <Section title="4. Google User Data">
                        <p>
                            When you sign in with Google, we access only your basic profile information (name, email
                            address, and profile picture) for the sole purpose of creating and authenticating your
                            account on our Service.
                        </p>
                        <p>
                            Our use and transfer of information received from Google APIs adheres to the{' '}
                            <a
                                href="https://developers.google.com/terms/api-services-user-data-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#1B4D3E] underline"
                            >
                                Google API Services User Data Policy
                            </a>
                            , including the Limited Use requirements. We do not sell your Google data, and we do not
                            use it for advertising. We only use it to provide the account and ordering features you
                            request.
                        </p>
                    </Section>

                    <Section title="5. How We Share Information">
                        <p>We do not sell your personal information. We share it only with trusted service providers who help us run the Service, namely:</p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li><strong>Stripe</strong> — to securely process payments.</li>
                            <li><strong>Resend</strong> — to deliver transactional emails.</li>
                            <li><strong>Google</strong> — for optional sign-in.</li>
                        </ul>
                        <p>We may also disclose information where required by law or to protect our rights and the safety of others.</p>
                    </Section>

                    <Section title="6. Data Retention">
                        <p>
                            We retain your account and order information for as long as your account is active or as
                            needed to provide the Service, comply with our legal obligations, resolve disputes, and
                            enforce our agreements. You may request deletion of your account at any time (see below).
                        </p>
                    </Section>

                    <Section title="7. Security">
                        <p>
                            We take reasonable technical and organisational measures to protect your information,
                            including encrypted connections and secure password storage. However, no method of
                            transmission or storage is completely secure, and we cannot guarantee absolute security.
                        </p>
                    </Section>

                    <Section title="8. Your Rights">
                        <p>
                            You may request access to, correction of, or deletion of your personal information, or
                            withdraw consent for Google sign-in by disconnecting our app in your{' '}
                            <a
                                href="https://myaccount.google.com/permissions"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#1B4D3E] underline"
                            >
                                Google Account permissions
                            </a>
                            . To exercise these rights, contact us using the details below.
                        </p>
                    </Section>

                    <Section title="9. Cookies">
                        <p>
                            We use essential cookies and similar technologies to keep you signed in and to operate the
                            Service. These are necessary for the site to function.
                        </p>
                    </Section>

                    <Section title="10. Children's Privacy">
                        <p>
                            Our Service is intended for businesses and adults. It is not directed to children under 18,
                            and we do not knowingly collect information from children.
                        </p>
                    </Section>

                    <Section title="11. Changes to This Policy">
                        <p>
                            We may update this Privacy Policy from time to time. Any changes will be posted on this page
                            with an updated "Last updated" date.
                        </p>
                    </Section>

                    <Section title="12. Contact Us">
                        <p>If you have questions about this Privacy Policy or your data, please contact us:</p>
                        <ul className="list-none space-y-1">
                            <li><strong>Henry's Aussie Fruit &amp; Veggies</strong></li>
                            <li>Sydney, Australia</li>
                            <li>Email: <a href="mailto:henrys3@bigpond.com" className="text-[#1B4D3E] underline">henrys3@bigpond.com</a></li>
                            <li>Phone: <a href="tel:+61414829929" className="text-[#1B4D3E] underline">+61 414 829 929</a></li>
                        </ul>
                    </Section>

                    <div className="pt-4 border-t border-slate-100">
                        <Link to="/" className="text-sm text-[#1B4D3E] underline" data-testid="privacy-home-link">← Back to Home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
